import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import useFetch from "../../../Component/Hooks/useFetch";

const EditWidgetService = ({ widgetId }) => {
  const user = useSelector((state) => state.userReducer);
  const userId = user.user._id;

  const [isLoading, setIsLoading] = useState(false);
  const [widgetData, setWidgetData] = useState({
    isOnline: "online",
    type: "Widget Service",
  });
  const [addOns, setAddOns] = useState([]);

  const { loading, data } = useFetch(
    `${process.env.REACT_APP_API_URL}/api/widgets/service/getOneWidget/${widgetId}`
  );

  useEffect(() => {
    if (data) {
      setWidgetData(data.widget);
      setAddOns(data.widget.addOns || []);
    }
  }, [data, widgetId]);

  const handleInputChange = (e) => {
    const value =
      e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setWidgetData({ ...widgetData, [e.target.name]: value });
  };

  const addAddOn = () => {
    setAddOns([...addOns, { name: "", priceType: "per_m2", price: 0 }]);
  };

  const removeAddOn = (index) => {
    setAddOns(addOns.filter((_, i) => i !== index));
  };

  const handleAddOnChange = (index, field, value) => {
    const updated = addOns.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setAddOns(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/service/edite/${widgetId}/user/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...widgetData, addOns }),
        }
      );
      if (response.ok) {
        toast.success("Widget édité avec succès");
      } else {
        switch (response.status) {
          case 401:
            toast.error("Vous n'êtes pas connecté");
            break;
          case 400:
            toast.error("Attention, un widget existe déjà avec ce nom.");
            break;
          default:
            toast.error("Une erreur est survenue");
            break;
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Une erreur est survenue lors de la mise à jour.");
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    "text-center block w-full rounded-lg border border-gray-200 p-2.5 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all";

  return (
    <div className="w-full max-w-2xl mx-auto py-8 px-4">
      {loading ? (
        <p className="text-center text-gray-400">Chargement...</p>
      ) : user.user.isPremium ? (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Titre */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Titre du simulateur *
            </label>
            <input
              className={inputClass}
              type="text"
              name="title"
              placeholder="Ex : Devis Peinture - Mon Entreprise"
              value={widgetData.title || ""}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Nom du service */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Nom du service *
            </label>
            <input
              className={inputClass}
              type="text"
              name="serviceName"
              placeholder="Ex : Peinture, Carrelage, Plomberie..."
              value={widgetData.serviceName || ""}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Afficher le prix */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Afficher le prix au client ?
            </label>
            <select
              className={inputClass}
              name="priceActive"
              value={widgetData.priceActive}
              onChange={handleInputChange}
            >
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </select>
          </div>

          {/* Section tarification */}
          {(widgetData?.priceActive === true ||
            widgetData?.priceActive === "true") && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Tarification au m²
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Prix min / m²
                  </label>
                  <div className="relative">
                    <input
                      className={inputClass}
                      type="number"
                      name="pricePerM2Min"
                      placeholder="15"
                      value={widgetData.pricePerM2Min || 0}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Prix max / m²
                  </label>
                  <div className="relative">
                    <input
                      className={inputClass}
                      type="number"
                      name="pricePerM2Max"
                      placeholder="35"
                      value={widgetData.pricePerM2Max || 0}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Section Options / Prestations */}
              <div className="border-t border-gray-200 pt-5">
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Options / Prestations supplémentaires
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Ajoutez des prestations que le client pourra cocher en
                    option. Deux types de tarif :
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 mt-2">
                    <div className="flex-1 bg-white border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-bold text-gray-700 mb-0.5">
                        Au m²
                      </p>
                      <p className="text-[11px] text-gray-400">
                        Le prix est multiplié par la surface.
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1 italic">
                        Ex : "Préparation du support" — 5€/m²
                      </p>
                    </div>
                    <div className="flex-1 bg-white border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-bold text-gray-700 mb-0.5">
                        Forfaitaire
                      </p>
                      <p className="text-[11px] text-gray-400">
                        Un prix fixe, quelle que soit la surface.
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1 italic">
                        Ex : "Nettoyage de chantier" — 150€
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2.5">
                  {addOns.map((addOn, index) => (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5"
                    >
                      <input
                        className="flex-1 rounded-lg border border-gray-200 p-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        type="text"
                        placeholder="Ex : Sous-couche, Nettoyage..."
                        value={addOn.name}
                        onChange={(e) =>
                          handleAddOnChange(index, "name", e.target.value)
                        }
                      />
                      <select
                        className="w-full sm:w-32 rounded-lg border border-gray-200 p-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        value={addOn.priceType}
                        onChange={(e) =>
                          handleAddOnChange(index, "priceType", e.target.value)
                        }
                      >
                        <option value="per_m2">Au m²</option>
                        <option value="fixed">Forfait</option>
                      </select>
                      <div className="relative w-full sm:w-28">
                        <input
                          className="w-full rounded-lg border border-gray-200 p-2 pr-7 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          type="number"
                          placeholder="0"
                          value={addOn.price}
                          onChange={(e) =>
                            handleAddOnChange(
                              index,
                              "price",
                              Number(e.target.value)
                            )
                          }
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                          €
                        </span>
                      </div>
                      {addOns.length > 1 && (
                        <button
                          type="button"
                          className="w-8 h-8 shrink-0 rounded-lg bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors self-center"
                          onClick={() => removeAddOn(index)}
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="w-full mt-3 p-2.5 rounded-lg text-sm font-medium border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-all"
                  onClick={addAddOn}
                >
                  + Ajouter une option
                </button>
              </div>
            </div>
          )}

          {/* Apparence */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Apparence
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Couleur bouton
                </label>
                <input
                  className={inputClass}
                  type="text"
                  name="btnColor"
                  placeholder="#000000"
                  value={widgetData.btnColor || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Couleur fond
                </label>
                <input
                  className={inputClass}
                  type="text"
                  name="backgroundColor"
                  placeholder="#111111"
                  value={widgetData.backgroundColor || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Couleur texte
                </label>
                <input
                  className={inputClass}
                  type="text"
                  name="labelColor"
                  placeholder="#ffffff"
                  value={widgetData.labelColor || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Contact
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Email
                </label>
                <input
                  className={inputClass}
                  type="email"
                  name="contactMail"
                  placeholder="contact@entreprise.fr"
                  value={widgetData.contactMail || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Téléphone
                </label>
                <input
                  className={inputClass}
                  type="tel"
                  name="contactPhone"
                  placeholder="06 12 34 56 78"
                  value={widgetData.contactPhone || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full p-3 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: widgetData.btnColor || "#000" }}
          >
            {isLoading ? "Chargement..." : "Enregistrer les modifications"}
          </button>
        </form>
      ) : null}
    </div>
  );
};

export default EditWidgetService;
